import React from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  useTheme,
  alpha,
  Button,
  Paper,
  Icon,
  useMediaQuery,
} from "@mui/material";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import heroDashboard from "../../../../assets/features/hero-dashboard.png";
import rawMaterials from "../../../../assets/features/raw-materials.png";
import recipeBuilder from "../../../../assets/features/recipe-builder.png";
import menuItems from "../../../../assets/features/menu-items.png";
import posSystem from "../../../../assets/features/pos-system.png";
import costTracking from "../../../../assets/features/cost-tracking.png";
import multiBranch from "../../../../assets/features/multi-branch.png";
import staffManagement from "../../../../assets/features/staff-management.png";

// Background Pattern Components
const GridPattern = () => (
  <Box
    sx={{
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundImage: `
        linear-gradient(to right, rgba(255, 255, 255, 0.05) 1px, transparent 1px),
        linear-gradient(to bottom, rgba(255, 255, 255, 0.05) 1px, transparent 1px)
      `,
      backgroundSize: "64px 64px",
      opacity: 0.3,
      zIndex: 0,
    }}
  />
);

const DotsPattern = () => (
  <Box
    sx={{
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundImage: `radial-gradient(circle at center, rgba(255, 255, 255, 0.1) 2px, transparent 2px)`,
      backgroundSize: "32px 32px",
      opacity: 0.3,
      zIndex: 0,
    }}
  />
);

const CircleGlow = () => (
  <Box
    sx={{
      position: "absolute",
      width: "60%",
      paddingBottom: "60%",
      background:
        "radial-gradient(circle at center, rgba(6, 182, 212, 0.15) 0%, transparent 70%)",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
      zIndex: 0,
      animation: "pulse 8s ease-in-out infinite",
      "@keyframes pulse": {
        "0%": {
          transform: "translate(-50%, -50%) scale(1)",
          opacity: 0.5,
        },
        "50%": {
          transform: "translate(-50%, -50%) scale(1.2)",
          opacity: 0.3,
        },
        "100%": {
          transform: "translate(-50%, -50%) scale(1)",
          opacity: 0.5,
        },
      },
    }}
  />
);

const featureBlocks = [
  {
    id: "raw-materials",
    title: "Track Raw Materials with Precision",
    description:
      "Manage your raw materials with batch-level control. Track expiry dates, supplier sources, and stock movement—so nothing goes unnoticed.",
    icon: "inventory_2",
    imageUrl: rawMaterials, // Placeholder - need actual image
    imagePosition: "right",
  },
  {
    id: "recipes",
    title: "Build Recipes That Power Your Kitchen",
    description:
      "Create recipe templates using real raw materials and track the cost of each API (assembled pantry item). Every change in cost or quantity updates your profitability in real-time.",
    icon: "restaurant_menu",
    imageUrl: recipeBuilder, // Placeholder - need actual image
    imagePosition: "left",
  },
  {
    id: "sellable-items",
    title: "From Kitchen to Menu: Sellable Item Creation",
    description:
      "Craft final menu items using our Bill of Materials system. Tie together your processed items, pricing, and expected yield—ready to sell with full traceability.",
    icon: "menu_book",
    imageUrl: menuItems, // Placeholder - need actual image
    imagePosition: "right",
  },
  {
    id: "pos",
    title: "Smart POS for Fast & Accurate Sales",
    description:
      "Run sales from a dedicated cashier interface tailored for speed and reliability. Menu items flow from your backend, and every sale updates inventory in real time.",
    icon: "point_of_sale",
    imageUrl: posSystem, // Placeholder - need actual image
    imagePosition: "left",
  },
  {
    id: "cost-tracking",
    title: "Real-Time Cost & Expiry Monitoring",
    description:
      "Track every ingredient's cost history and expiry to reduce waste and stay profitable. Set notifications for nearing expiry or major price shifts.",
    icon: "monitoring",
    imageUrl: costTracking, // Placeholder - need actual image
    imagePosition: "right",
  },
  {
    id: "multi-branch",
    title: "One Dashboard. All Branches. Full Control.",
    description:
      "Operate multiple locations with ease. Define branches, link warehouses, and monitor activity across every unit from a unified dashboard.",
    icon: "account_tree",
    imageUrl: multiBranch, // Placeholder - need actual image
    imagePosition: "left",
  },
  {
    id: "staff-management",
    title: "Assign Staff, Define Roles, Set Boundaries",
    description:
      "Create departments, assign staff, and set fine-grained access permissions. Ensure everyone sees exactly what they need—and nothing more.",
    icon: "group",
    imageUrl: staffManagement, // Placeholder - need actual image
    imagePosition: "right",
  },
  {
    id: "audit-trail",
    title: "Know Who Changed What, and Why",
    description:
      "Every recipe edit, cost update, and POS transaction is logged. Maintain accountability across your team and revert if needed.",
    icon: "history",
    imageUrl: costTracking, // Placeholder - need actual image
    imagePosition: "left",
  },
  {
    id: "templates",
    title: "Tailored to Fit Your Workflow",
    description:
      "Customize your recipe structures, BOM layouts, item units, and more. VenFlows adapts to your kitchen—not the other way around.",
    icon: "dashboard_customize",
    imageUrl: staffManagement, // Placeholder - need actual image
    imagePosition: "right",
  },
];

export const Features: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const scrollToFeatures = () => {
    const firstFeature = document.getElementById("raw-materials");
    if (firstFeature) {
      firstFeature.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <Box sx={{ overflow: "hidden" }}>
      {/* Hero Section */}
      <Box
        sx={{
          background: "linear-gradient(135deg, #000B18 0%, #001E3C 100%)",
          color: "white",
          pt: { xs: 12, md: 16 },
          pb: { xs: 10, md: 14 },
          position: "relative",
          overflow: "hidden",
        }}
      >
        <GridPattern />
        <DotsPattern />
        <Box
          sx={{
            position: "absolute",
            top: "10%",
            right: "5%",
            transform: "rotate(-15deg)",
          }}
        >
          <CircleGlow />
        </Box>
        <Box
          sx={{
            position: "absolute",
            bottom: "10%",
            left: "5%",
            transform: "rotate(15deg)",
          }}
        >
          <CircleGlow />
        </Box>
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            opacity: 0.4,
            background:
              "radial-gradient(circle at 20% 20%, rgba(6, 182, 212, 0.15) 0%, transparent 40%), radial-gradient(circle at 80% 80%, rgba(59, 130, 246, 0.15) 0%, transparent 40%)",
            zIndex: 0,
            animation: "float 15s ease-in-out infinite",
            "@keyframes float": {
              "0%": {
                transform: "translateY(0) scale(1)",
              },
              "50%": {
                transform: "translateY(-20px) scale(1.05)",
              },
              "100%": {
                transform: "translateY(0) scale(1)",
              },
            },
          }}
        />
        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <Typography
                  variant="h1"
                  sx={{
                    fontSize: { xs: "2.5rem", md: "3.5rem" },
                    fontWeight: 800,
                    mb: 3,
                    letterSpacing: "-0.02em",
                    color: "white",
                    fontFamily: "Montserrat",
                    textShadow: "0 4px 8px rgba(0, 0, 0, 0.15)",
                  }}
                >
                  Built for Modern Restaurant Operations
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    mb: 4,
                    color: "rgba(255,255,255,0.95)",
                    fontWeight: 500,
                    lineHeight: 1.5,
                    fontFamily: "Montserrat",
                    fontSize: { xs: "1.1rem", md: "1.25rem" },
                    textShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                  }}
                >
                  VenFlows gives restaurant teams full visibility and control
                  from raw materials to final sale—designed for real kitchens,
                  not spreadsheets.
                </Typography>
                <Button
                  variant="contained"
                  size="large"
                  onClick={scrollToFeatures}
                  endIcon={<Icon>arrow_downward</Icon>}
                  sx={{
                    bgcolor: "white",
                    color: "primary.main",
                    px: 4,
                    py: 1.5,
                    fontSize: "1.1rem",
                    borderRadius: "12px",
                    fontFamily: "Montserrat",
                    fontWeight: 600,
                    boxShadow: "0 8px 24px rgba(0,0,0,0.2)",
                    "&:hover": {
                      bgcolor: alpha(theme.palette.common.white, 0.9),
                      transform: "translateY(-2px)",
                      boxShadow: "0 12px 28px rgba(0,0,0,0.25)",
                    },
                    transition: "all 0.3s ease",
                  }}
                >
                  Explore Features
                </Button>
              </motion.div>
            </Grid>
            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                <Box
                  sx={{
                    p: { xs: 2, md: 4 },
                    position: "relative",
                    zIndex: 2,
                    isolation: "isolate",
                  }}
                >
                  <Box
                    component="img"
                    src={heroDashboard}
                    alt="Dashboard Overview"
                    sx={{
                      width: "100%",
                      height: "auto",
                      maxWidth: 600,
                      display: "block",
                      mixBlendMode: "normal",
                      transition: "transform 0.5s ease",
                      "&:hover": {
                        transform: "translateY(-8px)",
                      },
                    }}
                  />
                </Box>
              </motion.div>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Feature Blocks */}
      <Box sx={{ position: "relative", overflow: "hidden" }}>
        <DotsPattern />
        <Container
          maxWidth="lg"
          sx={{ py: { xs: 8, md: 12 }, position: "relative", zIndex: 1 }}
        >
          {featureBlocks.map((feature, index) => (
            <Box
              key={feature.id}
              id={feature.id}
              sx={{
                py: { xs: 6, md: 10 },
                scrollMarginTop: "80px",
                position: "relative",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: (theme) =>
                    theme.palette.mode === "dark"
                      ? `linear-gradient(135deg, ${alpha(
                          theme.palette.primary.dark,
                          0.1
                        )} 0%, ${alpha(
                          theme.palette.secondary.dark,
                          0.1
                        )} 100%)`
                      : `linear-gradient(135deg, ${alpha(
                          theme.palette.primary.light,
                          0.15
                        )} 0%, ${alpha(
                          theme.palette.secondary.light,
                          0.15
                        )} 100%)`,
                  borderRadius: { xs: 0, md: "40px" },
                  opacity: index % 2 === 0 ? 1 : 0,
                },
              }}
            >
              {index % 2 === 0 && (
                <Box
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "-20%",
                    transform: "translateY(-50%)",
                  }}
                >
                  <CircleGlow />
                </Box>
              )}
              {index % 2 === 1 && (
                <Box
                  sx={{
                    position: "absolute",
                    top: "50%",
                    right: "-20%",
                    transform: "translateY(-50%)",
                  }}
                >
                  <CircleGlow />
                </Box>
              )}

              <Grid
                container
                spacing={6}
                alignItems="center"
                direction={
                  isMobile
                    ? "column-reverse"
                    : feature.imagePosition === "left"
                    ? "row"
                    : "row-reverse"
                }
              >
                <Grid item xs={12} md={6}>
                  <motion.div
                    initial={{
                      opacity: 0,
                      x: feature.imagePosition === "left" ? -20 : 20,
                    }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5 }}
                    viewport={{ once: true }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        mb: 3,
                        gap: 2,
                      }}
                    >
                      <Box
                        sx={{
                          p: 2,
                          borderRadius: 3,
                          background: (theme) =>
                            `linear-gradient(135deg, ${alpha(
                              theme.palette.primary.main,
                              0.15
                            )} 0%, ${alpha(
                              theme.palette.secondary.main,
                              0.15
                            )} 100%)`,
                          backdropFilter: "blur(10px)",
                          border: "1px solid rgba(255, 255, 255, 0.1)",
                        }}
                      >
                        <Icon
                          sx={{
                            fontSize: 36,
                            background: (theme) =>
                              `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                            backgroundClip: "text",
                            WebkitBackgroundClip: "text",
                            WebkitTextFillColor: "transparent",
                          }}
                        >
                          {feature.icon}
                        </Icon>
                      </Box>
                      <Typography
                        variant="h3"
                        sx={{
                          fontWeight: 700,
                          fontFamily: "Montserrat",
                          background: (theme) =>
                            `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                          backgroundClip: "text",
                          WebkitBackgroundClip: "text",
                          WebkitTextFillColor: "transparent",
                        }}
                      >
                        {feature.title}
                      </Typography>
                    </Box>
                    <Typography
                      variant="h6"
                      sx={{
                        mb: 4,
                        lineHeight: 1.7,
                        fontFamily: "Montserrat",
                        color: (theme) =>
                          alpha(theme.palette.text.primary, 0.8),
                      }}
                    >
                      {feature.description}
                    </Typography>
                  </motion.div>
                </Grid>
                <Grid item xs={12} md={6}>
                  <motion.div
                    initial={{
                      opacity: 0,
                      x: feature.imagePosition === "left" ? 20 : -20,
                    }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5 }}
                    viewport={{ once: true }}
                  >
                    <Box
                      sx={{
                        p: { xs: 2, md: 4 },
                        position: "relative",
                        zIndex: 2,
                        isolation: "isolate",
                      }}
                    >
                      <Box
                        component="img"
                        src={feature.imageUrl}
                        alt={feature.title}
                        sx={{
                          width: "100%",
                          height: "auto",
                          display: "block",
                          transition: "all 0.4s ease",
                          mixBlendMode: "normal",
                          "&:hover": {
                            transform: "translateY(-8px)",
                          },
                        }}
                      />
                    </Box>
                  </motion.div>
                </Grid>
              </Grid>
            </Box>
          ))}
        </Container>
      </Box>

      {/* Closing Section */}
      <Box
        sx={{
          py: { xs: 8, md: 12 },
          background: "linear-gradient(135deg, #000B18 0%, #001E3C 100%)",
          borderRadius: {
            xs: "0px",
            md: "40px 40px 0 0",
          },
          position: "relative",
          overflow: "hidden",
          mt: 8,
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background:
              "radial-gradient(circle at 50% 50%, rgba(6, 182, 212, 0.03) 0%, transparent 70%)",
            zIndex: 0,
          },
        }}
      >
        <GridPattern />
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
          }}
        >
          <CircleGlow />
        </Box>

        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            <Typography
              variant="h2"
              align="center"
              sx={{
                fontWeight: 800,
                mb: 3,
                color: "white",
                fontFamily: "Montserrat",
                textShadow: "0 2px 4px rgba(0,0,0,0.2)",
              }}
            >
              One Platform. Every Operational Need Covered.
            </Typography>
            <Typography
              variant="h5"
              align="center"
              sx={{
                mb: 6,
                maxWidth: 800,
                mx: "auto",
                color: "rgba(255,255,255,0.9)",
                fontFamily: "Montserrat",
                textShadow: "0 2px 4px rgba(0,0,0,0.1)",
              }}
            >
              From raw stock to the cashier, VenFlows centralizes it all with
              intelligence and simplicity.
            </Typography>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                gap: 3,
                flexWrap: "wrap",
              }}
            >
              <Button
                variant="contained"
                size="large"
                onClick={() => navigate("/contact")}
                sx={{
                  px: 4,
                  py: 1.5,
                  fontSize: "1.1rem",
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                  borderRadius: "12px",
                  boxShadow: "0 8px 24px rgba(0,0,0,0.2)",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: "0 12px 28px rgba(0,0,0,0.25)",
                  },
                  transition: "all 0.3s ease",
                }}
              >
                Book a Demo
              </Button>
            </Box>
          </motion.div>
        </Container>
      </Box>
    </Box>
  );
};
